import React, { useState } from 'react';
import styled from 'styled-components';
import { Download, Play, FileVideo, FileText, Settings, CheckCircle, AlertCircle } from 'lucide-react';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.background};
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
`;

const ExportOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  margin-bottom: 8px;
  background: ${props => props.theme.surface};
`;

const OptionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OptionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => props.theme.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.primary};
`;

const OptionDetails = styled.div``;

const OptionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 2px;
`;

const OptionDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const PreviewSection = styled.div`
  margin-bottom: 16px;
`;

const PreviewButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border: 2px dashed ${props => props.theme.border};
  border-radius: 8px;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
    color: ${props => props.theme.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const SettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SettingLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
`;

const SettingValue = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  padding: 6px 8px;
  background: ${props => props.theme.surface};
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border};
`;

const ProgressContainer = styled.div`
  margin-top: 16px;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ProgressTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const ProgressPercentage = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${props => props.theme.border};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.primary};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: ${props => props.success ? props.theme.success : props.theme.textSecondary};
`;

const ExportAllButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: none;
  border-radius: 8px;
  background: ${props => props.theme.primary};
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary}dd;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ExportPanel = ({ project, onUpdate }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('');
  const [completedExports, setCompletedExports] = useState([]);

  const hasTranscript = project.transcript && project.transcript.length > 0;
  const hasTranslation = project.translation && project.translation.length > 0;
  const hasParts = project.parts && project.parts.length > 0;

  const exportOptions = [
    {
      id: 'video',
      title: 'Final Video',
      description: 'Export the complete video with all effects',
      icon: FileVideo,
      available: true,
      format: 'MP4'
    },
    {
      id: 'parts',
      title: 'Video Parts',
      description: 'Export all created parts as separate videos',
      icon: FileVideo,
      available: hasParts,
      format: 'MP4'
    },
    {
      id: 'subtitles-srt',
      title: 'Subtitles (SRT)',
      description: 'Export subtitles in SRT format',
      icon: FileText,
      available: hasTranscript,
      format: 'SRT'
    },
    {
      id: 'subtitles-json',
      title: 'Subtitles (JSON)',
      description: 'Export subtitles with timing data',
      icon: FileText,
      available: hasTranscript,
      format: 'JSON'
    },
    {
      id: 'translation',
      title: 'Translation',
      description: 'Export translated subtitles',
      icon: FileText,
      available: hasTranslation,
      format: 'SRT'
    }
  ];

  const handlePreview = () => {
    // This would open a preview window with the final video
    console.log('Opening preview...');
  };

  const handleExportSingle = async (optionId) => {
    setIsExporting(true);
    setExportProgress(0);
    setExportStatus(`Exporting ${optionId}...`);

    try {
      // Mock export process
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 200));
        setExportProgress(i);
      }

      setCompletedExports([...completedExports, optionId]);
      setExportStatus(`${optionId} exported successfully!`);
    } catch (error) {
      setExportStatus(`Error exporting ${optionId}: ${error.message}`);
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setExportProgress(0);
        setExportStatus('');
      }, 3000);
    }
  };

  const handleExportAll = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportStatus('Exporting all files...');

    try {
      const availableOptions = exportOptions.filter(option => option.available);
      const totalSteps = availableOptions.length * 100;
      let currentStep = 0;

      for (const option of availableOptions) {
        setExportStatus(`Exporting ${option.title}...`);
        
        for (let i = 0; i < 100; i += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          currentStep += 10;
          setExportProgress((currentStep / totalSteps) * 100);
        }
      }

      setCompletedExports(availableOptions.map(option => option.id));
      setExportStatus('All files exported successfully!');
    } catch (error) {
      setExportStatus(`Export error: ${error.message}`);
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setExportProgress(0);
        setExportStatus('');
      }, 3000);
    }
  };

  const getExportSettings = () => {
    const settings = project.settings || {};
    return {
      format: settings.format || '16:9',
      resolution: settings.resolution || '1080p',
      subtitles: hasTranscript ? 'Enabled' : 'Disabled',
      watermark: settings.watermark?.enabled ? 'Enabled' : 'Disabled',
      parts: hasParts ? `${project.parts.length} parts` : 'None'
    };
  };

  const exportSettings = getExportSettings();

  return (
    <Container>
      <Section>
        <SectionTitle>Final Preview</SectionTitle>
        <PreviewSection>
          <PreviewButton onClick={handlePreview}>
            <Play />
            Preview Final Video
          </PreviewButton>
        </PreviewSection>

        <SectionTitle>Export Settings</SectionTitle>
        <SettingsGrid>
          <SettingItem>
            <SettingLabel>Format</SettingLabel>
            <SettingValue>{exportSettings.format}</SettingValue>
          </SettingItem>
          <SettingItem>
            <SettingLabel>Resolution</SettingLabel>
            <SettingValue>{exportSettings.resolution}</SettingValue>
          </SettingItem>
          <SettingItem>
            <SettingLabel>Subtitles</SettingLabel>
            <SettingValue>{exportSettings.subtitles}</SettingValue>
          </SettingItem>
          <SettingItem>
            <SettingLabel>Watermark</SettingLabel>
            <SettingValue>{exportSettings.watermark}</SettingValue>
          </SettingItem>
          <SettingItem>
            <SettingLabel>Parts</SettingLabel>
            <SettingValue>{exportSettings.parts}</SettingValue>
          </SettingItem>
        </SettingsGrid>
      </Section>

      <Section>
        <SectionTitle>Export Options</SectionTitle>
        
        {exportOptions.map((option) => {
          const Icon = option.icon;
          const isCompleted = completedExports.includes(option.id);
          
          return (
            <ExportOption key={option.id}>
              <OptionInfo>
                <OptionIcon>
                  <Icon />
                </OptionIcon>
                <OptionDetails>
                  <OptionTitle>{option.title}</OptionTitle>
                  <OptionDescription>
                    {option.description} • {option.format}
                  </OptionDescription>
                </OptionDetails>
              </OptionInfo>
              
              <ExportButton
                variant={isCompleted ? 'success' : 'primary'}
                onClick={() => handleExportSingle(option.id)}
                disabled={!option.available || isExporting}
              >
                {isCompleted ? (
                  <>
                    <CheckCircle />
                    Exported
                  </>
                ) : (
                  <>
                    <Download />
                    Export
                  </>
                )}
              </ExportButton>
            </ExportOption>
          );
        })}

        {(isExporting || exportProgress > 0) && (
          <ProgressContainer>
            <ProgressHeader>
              <ProgressTitle>Export Progress</ProgressTitle>
              <ProgressPercentage>{Math.round(exportProgress)}%</ProgressPercentage>
            </ProgressHeader>
            <ProgressBar>
              <ProgressFill progress={exportProgress} />
            </ProgressBar>
            {exportStatus && (
              <StatusMessage success={exportStatus.includes('successfully')}>
                {exportStatus.includes('successfully') ? (
                  <CheckCircle size={14} />
                ) : exportStatus.includes('Error') ? (
                  <AlertCircle size={14} />
                ) : (
                  <Settings size={14} />
                )}
                {exportStatus}
              </StatusMessage>
            )}
          </ProgressContainer>
        )}
      </Section>

      <ExportAllButton
        onClick={handleExportAll}
        disabled={isExporting || exportOptions.filter(o => o.available).length === 0}
      >
        <Download />
        Export All Available Files
      </ExportAllButton>
    </Container>
  );
};

export default ExportPanel;